import {type JSX, type PropsWithChildren, useState} from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import useSendMail from '@hooks/mails/useSendMail'
import { Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'

import {
  ButtonModifier,
  ButtonTypes,
  IconAligns,
} from '@components/atoms/Button/Button'
import InputCheckbox from '@components/atoms/Form/InputCheckbox'
import InputDate from '@components/atoms/Form/InputDate'
import InputRadioGroup from '@components/atoms/Form/InputRadioGroup'
import { RadioButtonDirection } from '@components/atoms/Form/InputRadioGroup/InputRadioGroup'
import InputText from '@components/atoms/Form/InputText'
import InputTextarea from '@components/atoms/Form/InputTextarea'
import Col from '@components/atoms/Grid/Col'
import Row from '@components/atoms/Grid/Row'
import IconArrowRight from '@components/atoms/Icons/IconArrowRight'
import { Text, TextVariants } from '@components/atoms/Text/Text'

import { PropsWithClassName } from '@helper/PropsWithClassName'
import ThemeBasedLink from '@helper/ThemeBasedLink'

import { RoutesSunpoint } from '@definitions/routes/RoutesSunpoint'
import { RoutesWellmaxx } from '@definitions/routes/RoutesWellmaxx'

import { ButtonCol, Root, StyledButton } from './FormRevocation.styles'

export interface FormRevocationType {
  firstname: string
  lastname: string
  street: string
  number: string
  city: string
  zip: string
  birthdate: string
  email: string
  reasonForRevocation: string
  message: string
  privacy: boolean
  revoke: boolean
  [key: string]: unknown
}

interface Props extends PropsWithClassName {
  userData?: FormRevocationType
  handleSubmitForm: (data: FormRevocationType) => void
  formId: string
  handleSubmitted?: () => void
}

const FormRevocation: React.FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false)
  const [formSend, setFormSend] = useState<boolean>(false)
  const { handleSubmitForm, userData, formId, dataTestId, handleSubmitted } =
    props

  const { sendFormDataMail, result: resultSendMail } = useSendMail()

  const { register, handleSubmit, formState, setValue } =
    useForm<FormRevocationType>({
      resolver: yupResolver(
        yup.object().shape({
          firstname: yup.string().required('Dies ist ein Pflichtfeld.'),
          lastname: yup.string().required('Dies ist ein Pflichtfeld.'),
          street: yup.string().required('Dies ist ein Pflichtfeld.'),
          number: yup.string().required('Dies ist ein Pflichtfeld.'),
          zip: yup.string().required('Dies ist ein Pflichtfeld.'),
          city: yup.string().required('Dies ist ein Pflichtfeld.'),
          birthdate: yup
            .string()
            .test(
              'is-date',
              'Bitte trage dein Geburtsdatum im Format Tag. Monat Jahr ein.',
              (value) => {
                if (!value) return false
                return yup.date().isValidSync(value)
              }
            )
            .test('is-over-18', 'Du musst über 18 Jahre alt sein.', (value) => {
              if (!value) return false
              const birthdate = new Date(value)
              const today = new Date()
              const age = today.getFullYear() - birthdate.getFullYear()
              return age >= 18
            }),
          email: yup
            .string()
            .trim()
            .email(
              'Bitte trage deine E-Mail korrekt ein. Achte darauf, dass keine Leerzeichen und keine Umlaute enthalten sind und verwende keine Auto-Fill-Funktion.'
            )
            .required(
              'Bitte trage deine E-Mail korrekt ein. Achte darauf, dass keine Leerzeichen und keine Umlaute enthalten sind und verwende keine Auto-Fill-Funktion.'
            )
            .test(
              'is-valid-email',
              'Dies ist keine valide E-Mail-Adresse',
              (value) => {
                if (!value) return false
                return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
              }
            ),
          reasonForRevocation: yup
            .string()
            .required('Bitte fülle mindestens eines dieser Felder aus.'),
          message: yup.string(),
          privacy: yup
            .boolean()
            .oneOf(
              [true],
              'Bitte lese und akzeptiere unsere Datenschutzerklärung um fortzufahren.'
            ),
          revoke: yup
            .boolean()
            .oneOf(
              [true],
              'Bitte lese und akzeptiere unsere Datenschutzerklärung um fortzufahren.'
            ),
        })
      ) as Resolver<FormRevocationType>,
      defaultValues: userData,
    })

  const onSubmit = async (data: FormRevocationType) => {
    setLoading(true)
    await sendFormDataMail('Widerruf', data, 'revocation', [])
    await sendFormDataMail(
      'Eingangsbestätigung Widerruf Club-Mitgliedschaft',
      data,
      'revocation-confirm',
      []
    )
    setLoading(false)
    setFormSend(true)
    if (props.handleSubmitted) {
      props.handleSubmitted()
    }
  }

  return (
    <Root
      className={props.className}
      data-testid={dataTestId ? dataTestId : 'form-revocation-root'}
    >
      {!formSend && (
        <form
          onError={(error) => console.log(error)}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Row>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputText
                register={register('firstname')}
                forId={`${formId}-firstname`}
                label="Vorname"
                required={true}
                error={formState.errors.firstname ?? false}
                errorText={formState.errors?.firstname?.message}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputText
                register={register('lastname')}
                forId={`${formId}-lastname`}
                label="Nachname"
                required={true}
                error={formState.errors.lastname ?? false}
                errorText={formState.errors?.lastname?.message}
              />
            </Col>
            <Col xs={12} sm={10} md={4} lg={4}>
              <InputText
                register={register('street')}
                forId={`${formId}-street`}
                label="Straße"
                required={true}
                error={formState.errors.street ?? false}
                errorText={formState.errors?.street?.message}
              />
            </Col>
            <Col xs={12} sm={2} md={2} lg={2}>
              <InputText
                register={register('number')}
                forId={`${formId}-number`}
                label="Nr."
                required={true}
                error={formState.errors.number ?? false}
                errorText={formState.errors?.number?.message}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputText
                register={register('zip')}
                forId={`${formId}-zip`}
                label="PLZ"
                required={true}
                error={formState.errors.zip ?? false}
                errorText={formState.errors?.zip?.message}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputText
                register={register('city')}
                forId={`${formId}-city`}
                label="Stadt"
                required={true}
                error={formState.errors.city ?? false}
                errorText={formState.errors?.city?.message}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputDate
                value=""
                register={register('birthdate')}
                forId={`${formId}-birthdate`}
                label="Geburtsdatum"
                required={false}
                error={formState.errors.birthdate ?? false}
                errorText={formState.errors?.birthdate?.message}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={6}>
              <InputText
                register={register('email')}
                forId={`${formId}-email`}
                label="E-Mail-Adresse"
                required={true}
                error={formState.errors.email ?? false}
                errorText={formState.errors?.email?.message}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
              <InputRadioGroup
                label="Widerrufsgrund"
                direction={RadioButtonDirection.column}
                radios={[
                  // If changes are made, apply the adjustments here as well
                  // symfony/templates/emails/revocation_confirm.html.twig
                  {
                    label: 'Kein Bedarf',
                    value: 'no_need',
                    forId: `${formId}-reasonForRevocation-kein-bedarf`,
                  },
                  {
                    label: 'Unzufrieden',
                    value: 'dissatisfied',
                    forId: `${formId}-reasonForRevocation-unzufrieden`,
                  },
                  {
                    label: 'Sonstiges',
                    value: 'other',
                    forId: `${formId}-reasonForRevocation-sonstiges`,
                  },
                  {
                    label: 'Anderer Grund',
                    value: 'other_reasons',
                    forId: `${formId}-reasonForRevocation-anderer-grund`,
                  },
                ]}
                required={false}
                forId={`${formId}-reasonForRevocation`}
                name={`${formId}-reasonForRevocation`}
                error={formState.errors.reasonForRevocation ?? false}
                errorText={formState.errors?.reasonForRevocation?.message}
                onChange={(value) => {
                  setValue('reasonForRevocation', value)
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
              <InputTextarea
                register={register('message')}
                forId={`${formId}-message`}
                label="Bemerkung"
                error={formState.errors.message ?? false}
                errorText={formState.errors?.message?.message}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
              <InputCheckbox
                register={register('privacy')}
                forId={`${formId}-privacy`}
                error={formState.errors.privacy ?? false}
                errorText={formState.errors?.privacy?.message}
                required={true}
                label={
                  <>
                    Ja, ich habe die&nbsp;
                    <ThemeBasedLink
                      linkSunpoint={RoutesSunpoint.SUNPOINT__DATENSCHUTZ}
                      linkWellmaxx={RoutesWellmaxx.WELLMAXX__DATENSCHUTZ}
                      text="Datenschutzerklärung"
                    />
                    &nbsp;gelesen
                  </>
                }
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={12}>
              <InputCheckbox
                register={register('revoke')}
                forId={`${formId}-revoke`}
                required={true}
                error={formState.errors.revoke ?? false}
                errorText={formState.errors?.revoke?.message}
                label={
                  <>
                    Hiermit verzichte ich auf meine Club-Vorteile und widerrufe
                    meinen Online-Vertrag innerhalb der gesetzlichen
                    Widerrufsfrist von 14 Tagen.
                  </>
                }
              />
            </Col>
            <ButtonCol xs={12} sm={12} md={12} lg={12}>
              <StyledButton
                modifier={ButtonModifier.PRIMARY}
                buttonType={ButtonTypes.SUBMIT}
                disabled={formState.isSubmitting}
                icon={<IconArrowRight />}
                iconAlign={IconAligns.RIGHT}
                loading={loading}
              >
                Jetzt widerrufen
              </StyledButton>
            </ButtonCol>
          </Row>
        </form>
      )}
      {formSend && (
        <Text variant={TextVariants.lead}>
          <b>
            {resultSendMail === true &&
              'Wir haben deinen Widerruf erhalten und werden diesen prüfen.'}
            {resultSendMail === false && 'Es ist ein Fehler aufgetreten'}
          </b>
        </Text>
      )}
    </Root>
  )
}

export { FormRevocation }
